import { useState, useCallback } from "react";
import {
  Globe,
  Smartphone,
  Tablet,
  Monitor,
  Maximize2,
  Code2,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

interface WebPreviewBlockProps {
  html: string;
  title?: string;
}

type DeviceSize = "mobile" | "tablet" | "desktop" | "full";

const DEVICE_SIZES: Record<DeviceSize, { width: string; icon: React.ElementType; label: string }> = {
  mobile: { width: "375px", icon: Smartphone, label: "Mobile" },
  tablet: { width: "768px", icon: Tablet, label: "Tablet" },
  desktop: { width: "1024px", icon: Monitor, label: "Desktop" },
  full: { width: "100%", icon: Maximize2, label: "Full width" },
};

export function WebPreviewBlock({ html, title }: WebPreviewBlockProps) {
  const [activeTab, setActiveTab] = useState<"preview" | "source">("preview");
  const [deviceSize, setDeviceSize] = useState<DeviceSize>("full");

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(html);
    toast.success("HTML copied to clipboard");
  }, [html]);

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-border">
      {/* Header */}
      <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <span className="flex-1 text-sm font-medium truncate">{title || "Web Preview"}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-accent"
          title="Copy HTML"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tabs + Device toggles */}
      <div className="flex items-center justify-between border-b bg-muted/30 px-1">
        <div className="flex">
          <button
            type="button"
            className={`px-3 py-1.5 text-xs font-medium ${
              activeTab === "preview"
                ? "border-b-2 border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab("preview")}
          >
            Preview
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-xs font-medium ${
              activeTab === "source"
                ? "border-b-2 border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setActiveTab("source")}
          >
            <Code2 className="mr-1 inline h-3 w-3" />
            Source
          </button>
        </div>
        {activeTab === "preview" && (
          <div className="flex items-center gap-0.5 pr-1">
            {(Object.entries(DEVICE_SIZES) as [DeviceSize, typeof DEVICE_SIZES[DeviceSize]][]).map(
              ([size, { icon: Icon, label }]) => (
                <Button
                  key={size}
                  size="icon-xs"
                  variant={deviceSize === size ? "secondary" : "ghost"}
                  onClick={() => setDeviceSize(size)}
                  title={label}
                  type="button"
                >
                  <Icon className="h-3 w-3" />
                </Button>
              ),
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {activeTab === "preview" && (
        <div className="flex justify-center bg-muted/10 p-2">
          <div
            className="overflow-hidden rounded border border-border bg-white transition-all"
            style={{ width: DEVICE_SIZES[deviceSize].width, maxWidth: "100%" }}
          >
            <iframe
              srcDoc={html}
              sandbox="allow-scripts"
              className="h-[400px] w-full border-0"
              title={title || "Web preview"}
            />
          </div>
        </div>
      )}
      {activeTab === "source" && (
        <div className="max-h-[400px] overflow-auto p-3">
          <pre className="whitespace-pre-wrap font-mono text-xs text-foreground/80">{html}</pre>
        </div>
      )}
    </div>
  );
}
